import { z } from "zod";

export const DEFAULT_SCHEDULING_LOOKAHEAD_DAYS = 90;
export const DEFAULT_BOOKED_APPOINTMENTS_LOOKAHEAD_MONTHS = 15;

export const TimePreference = {
  MORNING: "morning",
  AFTERNOON: "afternoon",
} as const;
export type TimePreference = (typeof TimePreference)[keyof typeof TimePreference];

export const TimeOptionsIntent = {
  AFTER_DATE: "after_date",
  DATE_RANGE: "date_range",
  SPECIFIC_DATE: "specific_date",
  WEEK_OF: "week_of",
} as const;

export type TimeOptionsIntent = (typeof TimeOptionsIntent)[keyof typeof TimeOptionsIntent];

export const DayOfWeek = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
} as const;
export type DayOfWeek = (typeof DayOfWeek)[keyof typeof DayOfWeek];

export const timePreferenceSchema = z.nativeEnum(TimePreference);
export const timeOptionsIntentSchema = z.nativeEnum(TimeOptionsIntent);
export const dayOfWeekSchema = z.nativeEnum(DayOfWeek);

export const availabilityClockTimeSchema = z
  .string()
  .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, "Use a 24-hour clinic-local time in HH:mm format");

export const availabilityTimeBoundsShape = {
  earliestStartTime: availabilityClockTimeSchema
    .nullish()
    .describe(
      'Inclusive earliest appointment start time in clinic-local 24-hour HH:mm format. For example, "after 4 PM" becomes "16:00". Use null when the caller gave no lower time bound.'
    ),
  latestStartTime: availabilityClockTimeSchema
    .nullish()
    .describe(
      'Inclusive latest appointment start time in clinic-local 24-hour HH:mm format. For example, "before 10 AM" becomes "10:00". Use null when the caller gave no upper time bound.'
    ),
} as const;

export type AvailabilityTimeBounds = {
  readonly earliestStartTime?: string | null;
  readonly latestStartTime?: string | null;
};

export const availabilityDateRangeSchema = z.object({
  intent: z.literal(TimeOptionsIntent.DATE_RANGE),
  startDate: z.string(),
  endDate: z.string(),
  timePreference: z.array(timePreferenceSchema).optional(),
  daysOfWeek: z.array(dayOfWeekSchema).optional(),
  ...availabilityTimeBoundsShape,
});

export const availabilityAfterDateSchema = z.object({
  intent: z.literal(TimeOptionsIntent.AFTER_DATE),
  startDate: z.string(),
  timePreference: z.array(timePreferenceSchema).optional(),
  daysOfWeek: z.array(dayOfWeekSchema).optional(),
  ...availabilityTimeBoundsShape,
});

export const availabilitySpecificDateSchema = z.object({
  intent: z.literal(TimeOptionsIntent.SPECIFIC_DATE),
  date: z.string(),
  timePreference: z.array(timePreferenceSchema).optional(),
  ...availabilityTimeBoundsShape,
});

export const availabilityWeekOfSchema = z.object({
  intent: z.literal(TimeOptionsIntent.WEEK_OF),
  weekOfDate: z.string(),
  timePreference: z.array(timePreferenceSchema).optional(),
  daysOfWeek: z.array(dayOfWeekSchema).optional(),
  ...availabilityTimeBoundsShape,
});

function validateAvailabilityTimeBounds(windows: AvailabilityTimeBounds[], ctx: z.RefinementCtx): void {
  windows.forEach((window, index) => {
    if (window.earliestStartTime && window.latestStartTime && window.earliestStartTime > window.latestStartTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "earliestStartTime must not be later than latestStartTime",
        path: [index, "latestStartTime"],
      });
    }
  });
}

export const scheduleAvailabilitySchema = z
  .array(
    z.discriminatedUnion("intent", [
      availabilityAfterDateSchema,
      availabilitySpecificDateSchema,
      availabilityWeekOfSchema,
      availabilityDateRangeSchema,
    ])
  )
  .superRefine(validateAvailabilityTimeBounds);

export type AvailabilityDateRange = z.infer<typeof availabilityDateRangeSchema>;
export type AvailabilityAfterDate = z.infer<typeof availabilityAfterDateSchema>;
export type AvailabilitySpecificDate = z.infer<typeof availabilitySpecificDateSchema>;
export type AvailabilityWeekOf = z.infer<typeof availabilityWeekOfSchema>;
export type ScheduleAvailability = z.infer<typeof scheduleAvailabilitySchema>;

export const DateRangeSchema = z.object({
  ...availabilityDateRangeSchema.shape,
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
});

export const AfterDateSchema = z.object({
  ...availabilityAfterDateSchema.shape,
  startDate: z.coerce.date(),
});

export const SpecificDateSchema = z.object({
  ...availabilitySpecificDateSchema.shape,
  date: z.coerce.date(),
});

export const WeekOfSchema = z.object({
  ...availabilityWeekOfSchema.shape,
  weekOfDate: z.coerce.date(),
});

export type DateRangeSchema = z.infer<typeof DateRangeSchema>;
export type AfterDateSchema = z.infer<typeof AfterDateSchema>;
export type SpecificDateSchema = z.infer<typeof SpecificDateSchema>;
export type WeekOfSchema = z.infer<typeof WeekOfSchema>;

export const timeOptionsSchema = z
  .array(z.discriminatedUnion("intent", [AfterDateSchema, SpecificDateSchema, WeekOfSchema, DateRangeSchema]))
  .superRefine(validateAvailabilityTimeBounds);
export type TimeOptionsSchema = z.infer<typeof timeOptionsSchema>;
